import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Button,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    Avatar,
    Pagination,
    CircularProgress,
    Tooltip,
    Switch
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    MoreVert as MoreVertIcon,
    Token as TokenIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    AccountBalance as AccountBalanceIcon,
    TrendingUp as TrendingUpIcon,
    People as PeopleIcon,
    AttachMoney as AttachMoneyIcon,
    PersonAdd as PersonAddIcon,
    Lock as LockIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getQAIUsers, toggleQAIUserStatus, resetQAIUserPassword } from '../../api/admin_api';
import CreditTokensModal from './CreditTokensModal';
import CreateQAIUserModal from './CreateQAIUserModal';
import ResetPasswordModal from './ResetPasswordModal';

// Embedded theme
const theme = {
    colors: {
        bg: { primary: '#191b20', secondary: '#292b2f', tertiary: '#333539' },
        text: { primary: 'rgba(255,255,255,1)', secondary: 'rgba(255,255,255,0.9)', tertiary: 'rgba(255,255,255,0.7)', quaternary: 'rgba(255,255,255,0.5)' },
        accent: { primary: '#7c3aed', secondary: '#5b21b6', tertiary: '#a78bfa' },
        border: { primary: 'rgba(255,255,255,0.1)', secondary: 'rgba(255,255,255,0.05)' },
        status: { success: '#22c55e', error: '#ef4444', warning: '#f59e0b', info: '#3b82f6' }
    },
    gradients: { primaryAccent: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' },
    borderRadius: { sm: '8px', md: '12px' }
};

const AdminQAIUserManagement = forwardRef((props, ref) => {
    // State management
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [creditModalOpen, setCreditModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
    const [stats, setStats] = useState({
        totalUsers: 0,
        activeUsers: 0,
        totalTokensSold: 0,
        totalRevenue: 0
    });

    const rowsPerPage = 10;

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
        openCreditModal: (user) => {
            setSelectedUser(user);
            setCreditModalOpen(true);
        }
    }), []);

    // Fetch Q AI users on mount
    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const result = await getQAIUsers();
            if (result.success) {
                setUsers(result.data || []);
                // Calculate stats
                const userData = result.data || [];
                setStats({
                    totalUsers: userData.length,
                    activeUsers: userData.filter(u => u.isActive).length,
                    totalTokensSold: userData.reduce((sum, u) => sum + (u.tokensUsed || 0), 0),
                    totalRevenue: userData.reduce((sum, u) => sum + (u.tokensUsed || 0) * 0.01, 0) // Assuming $0.01 per token
                });
            } else {
                toast.error(result.error || 'Failed to fetch Q AI users');
            }
        } catch (error) {
            console.error('Error fetching Q AI users:', error);
            toast.error('Failed to fetch Q AI users');
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchUsers();
        setRefreshing(false);
        toast.success('Data refreshed');
    };

    const handleMenuOpen = (event, user) => {
        setAnchorEl(event.currentTarget);
        setSelectedUser(user);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleCreditTokens = () => {
        handleMenuClose();
        setCreditModalOpen(true);
    };

    const handleResetPassword = () => {
        handleMenuClose();
        setResetPasswordModalOpen(true);
    };

    const handleToggleStatus = async () => {
        if (!selectedUser) return;
        handleMenuClose();

        const newStatus = !selectedUser.isActive;
        const result = await toggleQAIUserStatus(selectedUser.userId, newStatus);

        if (result.success) {
            toast.success(result.message);
            fetchUsers(); // Refresh list
        } else {
            toast.error(result.error || 'Failed to toggle status');
        }
    };

    const handleCreditSuccess = () => {
        setCreditModalOpen(false);
        fetchUsers(); // Refresh to show new balance
    };

    const handleCreateSuccess = () => {
        setCreateModalOpen(false);
        fetchUsers(); // Refresh to show new user
    };

    // Filter users
    const filteredUsers = users.filter(user => {
        const matchesSearch = !searchQuery ||
            user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.userId?.toString().includes(searchQuery);

        const matchesStatus = statusFilter === 'all' ||
            (statusFilter === 'active' && user.isActive) ||
            (statusFilter === 'inactive' && !user.isActive);

        return matchesSearch && matchesStatus;
    });

    // Pagination
    const paginatedUsers = filteredUsers.slice((page - 1) * rowsPerPage, page * rowsPerPage);
    const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Box sx={{ p: 3, background: theme.colors.bg.primary, minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ color: theme.colors.text.primary, fontWeight: 700 }}>
                        Q AI User Management
                    </Typography>
                    <Typography sx={{ color: theme.colors.text.quaternary, mt: 0.5 }}>
                        Manage Q AI users and token credits
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setCreateModalOpen(true)}
                        sx={{
                            background: theme.gradients.primaryAccent,
                            '&:hover': { background: theme.colors.accent.secondary }
                        }}
                    >
                        Create User
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={refreshing}
                        sx={{
                            borderColor: theme.colors.border.primary,
                            color: theme.colors.text.secondary,
                            '&:hover': { borderColor: theme.colors.accent.primary }
                        }}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ background: theme.colors.status.info, width: 48, height: 48 }}>
                                    <PeopleIcon />
                                </Avatar>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                        Total Users
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontSize: '1.5rem', fontWeight: 700 }}>
                                        {stats.totalUsers}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ background: theme.colors.status.success, width: 48, height: 48 }}>
                                    <CheckCircleIcon />
                                </Avatar>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                        Active Users
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontSize: '1.5rem', fontWeight: 700 }}>
                                        {stats.activeUsers}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ background: theme.colors.accent.primary, width: 48, height: 48 }}>
                                    <TokenIcon />
                                </Avatar>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                        Tokens Used
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontSize: '1.5rem', fontWeight: 700 }}>
                                        {stats.totalTokensSold.toLocaleString()}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ background: theme.colors.status.warning, width: 48, height: 48 }}>
                                    <AttachMoneyIcon />
                                </Avatar>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                        Est. Revenue
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontSize: '1.5rem', fontWeight: 700 }}>
                                        ${stats.totalRevenue.toFixed(2)}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            placeholder="Search by email or user ID..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: theme.colors.text.quaternary }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    background: theme.colors.bg.tertiary,
                                    '& fieldset': { borderColor: theme.colors.border.primary }
                                },
                                '& .MuiInputBase-input': { color: theme.colors.text.primary }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: theme.colors.text.quaternary }}>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Status"
                                onChange={(e) => setStatusFilter(e.target.value)}
                                sx={{
                                    background: theme.colors.bg.tertiary,
                                    color: theme.colors.text.primary,
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.colors.border.primary }
                                }}
                            >
                                <MenuItem value="all">All Status</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Users Table */}
            <TableContainer component={Paper} sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                {loading && <LinearProgress />}
                <Table>
                    <TableHead>
                        <TableRow sx={{ background: theme.colors.bg.tertiary }}>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>User</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>Token Balance</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>Tokens Used</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>Last Active</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }}>Created</TableCell>
                            <TableCell sx={{ color: theme.colors.text.tertiary, fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedUsers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <Typography sx={{ color: theme.colors.text.quaternary }}>
                                        {loading ? 'Loading...' : 'No Q AI users found'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedUsers.map((user) => (
                                <TableRow key={user.userId} hover sx={{ '&:hover': { background: theme.colors.bg.tertiary } }}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar sx={{ background: theme.gradients.primaryAccent, width: 36, height: 36 }}>
                                                {user.email?.charAt(0).toUpperCase() || '?'}
                                            </Avatar>
                                            <Box>
                                                <Typography sx={{ color: theme.colors.text.primary, fontWeight: 500 }}>
                                                    {user.email || 'Unknown'}
                                                </Typography>
                                                <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                                    ID: {user.userId}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={<TokenIcon sx={{ fontSize: 16 }} />}
                                            label={user.tokenBalance?.toLocaleString() || 0}
                                            sx={{
                                                background: theme.colors.accent.primary + '20',
                                                color: theme.colors.accent.tertiary,
                                                fontWeight: 600
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ color: theme.colors.text.secondary }}>
                                            {user.tokensUsed?.toLocaleString() || 0}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={user.isActive ? <CheckCircleIcon /> : <CancelIcon />}
                                            label={user.isActive ? 'Active' : 'Inactive'}
                                            size="small"
                                            sx={{
                                                background: user.isActive ? theme.colors.status.success + '20' : theme.colors.status.error + '20',
                                                color: user.isActive ? theme.colors.status.success : theme.colors.status.error
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.85rem' }}>
                                            {formatDate(user.lastActiveAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.85rem' }}>
                                            {formatDate(user.createdAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            onClick={(e) => handleMenuOpen(e, user)}
                                            sx={{ color: theme.colors.text.tertiary }}
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: theme.colors.text.secondary,
                                '&.Mui-selected': {
                                    background: theme.colors.accent.primary,
                                    color: theme.colors.text.primary
                                }
                            }
                        }}
                    />
                </Box>
            )}

            {/* Action Menu (F6) */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        background: theme.colors.bg.secondary,
                        border: `1px solid ${theme.colors.border.primary}`
                    }
                }}
            >
                <MenuItem onClick={handleCreditTokens} sx={{ color: theme.colors.text.primary }}>
                    <TokenIcon sx={{ mr: 1, color: theme.colors.accent.tertiary }} />
                    Credit Tokens
                </MenuItem>
                <MenuItem onClick={handleResetPassword} sx={{ color: theme.colors.text.primary }}>
                    <LockIcon sx={{ mr: 1, color: theme.colors.status.warning }} />
                    Reset Password
                </MenuItem>
                <MenuItem onClick={handleToggleStatus} sx={{ color: theme.colors.text.primary }}>
                    {selectedUser?.isActive ? (
                        <>
                            <CancelIcon sx={{ mr: 1, color: theme.colors.status.error }} />
                            Disable Account
                        </>
                    ) : (
                        <>
                            <CheckCircleIcon sx={{ mr: 1, color: theme.colors.status.success }} />
                            Enable Account
                        </>
                    )}
                </MenuItem>
            </Menu>

            {/* Credit Tokens Modal (F4) */}
            <CreditTokensModal
                open={creditModalOpen}
                user={selectedUser}
                onClose={() => setCreditModalOpen(false)}
                onSuccess={handleCreditSuccess}
            />

            {/* Create Q AI User Modal (F4 - New) */}
            <CreateQAIUserModal
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onSuccess={handleCreateSuccess}
            />

            {/* Reset Password Modal */}
            <ResetPasswordModal
                open={resetPasswordModalOpen}
                user={selectedUser}
                onClose={() => setResetPasswordModalOpen(false)}
                onSuccess={() => {
                    setResetPasswordModalOpen(false);
                    toast.success('Password reset successfully');
                }}
            />
        </Box>
    );
});

AdminQAIUserManagement.displayName = 'AdminQAIUserManagement';

export default AdminQAIUserManagement;
